import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { type ColumnDef } from "@tanstack/react-table";
import { KeyRound, RefreshCw, Search, Check, X } from "lucide-react";
import { useAppRoles } from "@/hooks/use-app-roles";
import type { AppRoleBinding } from "@/types/org";
import { RoleBadge } from "@/components/shared/RoleBadge";
import { OrgUnitTypeBadge } from "@/components/shared/OrgUnitTypeBadge";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { Button, Input } from "@fox1ck-org/ui";
import type { OrgUnitType } from "@/types/org";

const MAX_VISIBLE_ROLES = 5;

function formatAge(dateStr: string): string {
  const now = Date.now();
  const created = new Date(dateStr).getTime();
  const diffMs = now - created;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "только что";
  if (diffMin < 60) return `${diffMin}m`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 30) return `${diffDays}d`;
  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths}mo`;
}

const columns: ColumnDef<AppRoleBinding>[] = [
  {
    accessorKey: "clientId",
    header: "Клиент",
    cell: ({ row }) => (
      <Link
        to={`/app-roles/${row.original.name}`}
        className="flex items-center gap-2 font-medium text-foreground hover:text-primary"
      >
        <KeyRound className="h-4 w-4 text-muted-foreground" />
        {row.original.clientId}
      </Link>
    ),
  },
  {
    accessorKey: "roles",
    header: "Роли",
    cell: ({ row }) => {
      const roles = row.original.roles;
      const visible = roles.slice(0, MAX_VISIBLE_ROLES);
      const remaining = roles.length - MAX_VISIBLE_ROLES;
      return (
        <div className="flex flex-wrap items-center gap-1">
          {visible.map((r) => (
            <RoleBadge key={r.name} name={r.name} description={r.description} />
          ))}
          {remaining > 0 && (
            <span className="text-xs text-muted-foreground">+{remaining} more</span>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "orgUnitTypes",
    header: "Типы подразделений",
    cell: ({ row }) => (
      <div className="flex flex-wrap gap-1">
        {row.original.orgUnitTypes.map((t) => (
          <OrgUnitTypeBadge key={t} type={t as OrgUnitType} />
        ))}
      </div>
    ),
  },
  {
    accessorKey: "status.ready",
    header: "Готово",
    cell: ({ row }) =>
      row.original.status.ready ? (
        <Check className="h-4 w-4 text-green-400" />
      ) : (
        <X className="h-4 w-4 text-red-400" />
      ),
  },
  {
    accessorKey: "createdAt",
    header: "Возраст",
    cell: ({ row }) => (
      <span className="text-muted-foreground">{formatAge(row.original.createdAt)}</span>
    ),
  },
];

export default function AppRoles() {
  const [search, setSearch] = useState("");
  const { data, isLoading, refetch } = useAppRoles();
  const navigate = useNavigate();

  const bindings = useMemo(() => data?.items ?? [], [data]);

  const filtered = useMemo(() => {
    if (!search) return bindings;
    const q = search.toLowerCase();
    return bindings.filter(
      (b) =>
        b.clientId.toLowerCase().includes(q) ||
        b.roles.some((r) => r.name.toLowerCase().includes(q))
    );
  }, [bindings, search]);

  return (
    <div className="space-y-6">
      <PageHeader title="Роли приложений" description="CRD AppRoleBinding задают клиентские роли Keycloak и protocol mappers">
        <Button variant="ghost" size="icon" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </PageHeader>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Поиск по клиенту или роли…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <span className="text-sm text-muted-foreground">
          {filtered.length} binding{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        isLoading={isLoading}
        onRowClick={(row) => navigate(`/app-roles/${row.name}`)}
        emptyState={
          <EmptyState
            icon={<KeyRound className="h-10 w-10" />}
            title={search ? "Ничего не нашлось" : "AppRoleBinding нет"}
            description={
              search
                ? "Попробуйте другой запрос."
                : "CRD AppRoleBinding появятся здесь после развёртывания в кластере."
            }
          />
        }
      />
    </div>
  );
}
